import {computed, ref, watchEffect} from 'vue';
import {
  type ChallengeDTO,
  type ChallengeSessionDTO,
  type ChallengeTaskDTO, ReccoChallengeSessionStateDTO} from '@/ts/types/dto/recco/challenge';
import {useApi} from '@/ts/composables/stateful/use-api';
import {ReccoRecommendationTypeDTO} from '@/ts/types/dto/recco/recommendation.dto';
import type {ReccoArticleDTO} from '@/ts/types/dto/recco/article.dto';
import type {ReccoAudioDTO} from '@/ts/types/dto/recco/audio.dto';
import type {ReccoVideoDTO} from '@/ts/types/dto/recco/video.dto';
import type {ReccoQuestionnaireResponse} from '@/ts/types/dto/recco/questionnaire.dto';
import type {ReccoTrackingActivityDTO} from '@/ts/types/dto/recco/tracking-activity';
import type {ChallengeSession, ChallengeTaskItem} from '@/ts/types/component/recco/challenge.type';
import {useI18n} from 'vue-i18n';
import {extractError} from '@/ts/composables/pure/use-loading-data-ref';

function createCacheId(recommendationType: ReccoRecommendationTypeDTO, itemId: string, catalogId: string): string {
  return `${recommendationType}-${itemId}-${catalogId}`;
};

/** Fetches and caches challenge detail data including session tasks and their recommendation payloads */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useChallengeDetailData(challengeId: number, getSessionId: () => string) {
  const api = useApi();
  const {t} = useI18n();

  const initialLoading = ref<boolean>(true);
  const loading = ref<boolean>(true);
  const error = ref<string|null>(null);

  const challenge = ref<ChallengeDTO>();

  const fetchChallenge = async(): Promise<void> => {
    loading.value = true;
    try {
      challenge.value = await api.content.getChallenge(challengeId);
    } catch (err: unknown) {
      error.value = extractError(err, t);
    }
  };

  const session = computed((): ChallengeSessionDTO | null => {
    return (challenge.value?.sessions ?? []).find(sessionItem => {
      return sessionItem.id.toString() === getSessionId();
    }) ?? null;
  });

  const visibleTasks = computed((): ChallengeTaskDTO[] => {
    const allTasks = (session.value?.tasks ?? []);
    const index = allTasks.findIndex((task) =>
      task.state !== ReccoChallengeSessionStateDTO.COMPLETED &&
      task.recommendation.type !== ReccoRecommendationTypeDTO.Questionnaire);
    return index === -1 ? allTasks : allTasks.slice(0, index + 1);
  });

  const cache = new Map<string, Promise<ReccoArticleDTO|ReccoAudioDTO|ReccoVideoDTO|ReccoQuestionnaireResponse|ReccoTrackingActivityDTO>>();
  const publicData = ref<ChallengeSession>();

  watchEffect((): void => {
    const promises: Promise<ReccoArticleDTO|ReccoAudioDTO|ReccoVideoDTO|ReccoQuestionnaireResponse|ReccoTrackingActivityDTO>[] = [];
    visibleTasks.value.forEach((task) => {
      const recommendationType = task.recommendation.type;
      const recommendationId = task.recommendation.id;
      const cacheId = createCacheId(recommendationType, recommendationId.itemId, recommendationId.catalogId);
      if (cache.has(cacheId)) {
        promises.push(cache.get(cacheId)!);
      } else {
        switch (recommendationType) {
          case ReccoRecommendationTypeDTO.Article:
            promises.push(api.content.getArticle(recommendationId.catalogId));
            break;
          case ReccoRecommendationTypeDTO.Video:
            promises.push(api.content.getVideo(recommendationId.catalogId));
            break;
          case ReccoRecommendationTypeDTO.Audio:
            promises.push(api.content.getAudio(recommendationId.catalogId));
            break;
          case ReccoRecommendationTypeDTO.Questionnaire:
            promises.push(api.content.getQuestionnaireById(recommendationId.itemId));
            break;
          default:
            /* not needed */
        }
      }
    });

    const awaitPromises = async(): Promise<void> => {
      try {
        const results = await Promise.all(promises);
        visibleTasks.value.forEach((task, index) => {
          const cacheId = createCacheId(task.recommendation.type, task.recommendation.id.itemId, task.recommendation.id.catalogId);
          cache.set(cacheId, promises[index]);
        });
        if (challenge.value) {
          publicData.value = {
            ...session.value!,
            challengeId: challenge.value.id,
            challengeHeadline: challenge.value.headline,
            totalNumberOfTasks: session.value?.tasks.length ?? 0,
            indexOfSession: challenge.value.sessions.findIndex(sessionItem => sessionItem.id === session.value?.id),
            visibleTasks: visibleTasks.value.map<ChallengeTaskItem>((rawTask, index) => {
              return {
                task: rawTask,
                recommendationPayload: results[index],
              };
            }),
          };
        }
      } catch (err: unknown) {
        error.value = extractError(err, t);
      } finally {
        if (session.value) {
          loading.value = false;
          initialLoading.value = false;
        }
      }
    };

    void awaitPromises();
  });

  void fetchChallenge();

  return {
    loading,
    initialLoading,
    error,
    session: publicData,
    challenge,
    fetchChallenge,
  };
}